import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * A flexible card container component with semantic structure.
 * Provides a bordered container with consistent spacing and shadow.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 * 
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Card Title</CardTitle>
 *     <CardDescription>Card description text</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     Main card content goes here
 *   </CardContent>
 *   <CardFooter>
 *     Footer actions or info
 *   </CardFooter>
 * </Card>
 * ```
 */
function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Card header section containing title, description, and optional actions.
 * Uses CSS Grid for flexible layout with action positioning.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 */
function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Primary title element for the card header.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 */
function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  )
}

/**
 * Descriptive text element for the card header.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 */
function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

/**
 * Action container positioned in the top-right of the card header.
 * Automatically positions using CSS Grid when present.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 */
function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Main content area of the card with consistent padding.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 */
function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6', className)}
      {...props}
    />
  )
}

/**
 * Footer section for actions or additional information.
 * Includes border-top styling when preceded by content.
 * 
 * @param className - Additional CSS classes to apply
 * @param props - Additional HTML div attributes
 */
function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
